import ENDPOINTS from "./Endpoints";
import {
  KeragonJobResponse,
  KeragonPatient,
  KeragonProxyRequest,
  KeragonProxyResponse,
  KeragonTrigger,
  KeragonWorkflow,
  KeragonWorkflowUpsert,
} from "../entities/keragon";
import {
  NUTBaseApiAffiliateRequest,
  IDBaseApi,
  PlatformBaseApiNoAuth,
  NUTBaseApi,
  PlatformBaseApi,
  PnoeBaseApi,
  PnoeAgentApi,
  BreathBaseApi,
  BreathBaseApiAffiliateRequest,
} from "./AppApiManager";
import axios from "axios";
import { UserDataType } from "../../modules/authentication/components/SignInForm";
import CONFIG from "../../navigation/Configuration";
import {
  PnoeBreathConfiguration,
  PnoeParticipantUpdate,
  toDecimalString,
} from "../../modules/platform/entities/pnoeMeasurement";
import {
  HistoryInbodyBiomarker,
  HistoryInbodyListItem,
  HistoryListParams,
  HistoryPage,
  HistoryPrescriptionDetail,
  HistoryPrescriptionListItem,
  HistoryServiceDetail,
  HistoryServiceListItem,
} from "../../modules/platform/entities/history";

export const loginUser = async (data: UserDataType) => {
  const client = await PlatformBaseApiNoAuth();

  if (!client) {
    return null;
  }
  const response = await client.post(ENDPOINTS.PLATFORM.LOGIN_URL, data);
  return response.data;
};

export const getReportDataPlatform = async (
  id: string,
  type: string,
  lang: string
) => {
  const client = await PlatformBaseApiNoAuth();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_REPORT_DATA_PLATFORM}/${id}/${type}/${lang}`
  );

  return response.data;
};

export const getReportDataUser = async (
  id: string,
  type: string,
  lang: string
) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_REPORT_DATA}/${id}/${type}/${lang}`
  );

  return response.data;
};

export const GetNewReport = async (id: string) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_NEW_REPORT}${id}`
  );

  return response.data;
};

export const GetNewReportDataPlatform = async (id: string) => {
  const client = await PlatformBaseApiNoAuth();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_NEW_REPORT}${id}`
  );

  return response.data;
};

export const getUserInfo = async () => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(ENDPOINTS.PLATFORM.GET_USER_INFO);
  return response.data;
};

export const settings = async () => {
  const client = await NUTBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(ENDPOINTS.NUTRITION.SETTINGS_URL);
  return response.data;
};

export const getCustomerSettings = async () => {
  const client = await NUTBaseApiAffiliateRequest();
  if (!client) {
    return null;
  }
  const response = await client.get(ENDPOINTS.NUTRITION.SETTINGS_URL);
  return response.data;
};

export const updateAffiliateUnitType = async (unitType: number) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.post(
    `${ENDPOINTS.PLATFORM.SET_UNIT_TYPE}${unitType}`
  );

  return response.data;
};

export const getCustomers = async (options?: { bustCache?: boolean }) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  // The GetCustomers response ships no Cache-Control, which leaves it open to a
  // browser/proxy caching a stale (sometimes empty) 200. Ask intermediaries to
  // revalidate, and add a unique query param on the retry to fully bypass caches.
  const url = options?.bustCache
    ? `${ENDPOINTS.PLATFORM.GET_CUSTOMERS}?_=${Date.now()}`
    : `${ENDPOINTS.PLATFORM.GET_CUSTOMERS}`;
  const response = await client.get(url, {
    headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
  });

  return response.data;
};

export const getAiSummary = async (
  customerId: string,
  agentSlug: string,
  signal?: AbortSignal
) => {
  const client = await PnoeBaseApi();
  if (!client) {
    return null;
  }

  const formData = new FormData();
  formData.append("agentSlug", agentSlug);
  formData.append("text", "Please give me the summary");

  const response = await client.post(
    `/api/v2/AI/UseAiChat/${encodeURIComponent(customerId)}`,
    formData,
    { signal }
  );

  return response.data;
};

export type InbodyAccount = {
  inBodyAPIKey: string;
  inBodyAPIUserName: string;
  inBodyAPIURL: string;
};

export const getInbodyAccount = async () => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(ENDPOINTS.PLATFORM.GET_INBODY_ACCOUNT);

  return response.data;
};

export const setInbodyAccount = async (data: InbodyAccount) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.post(
    ENDPOINTS.PLATFORM.SET_INBODY_ACCOUNT,
    data
  );

  return response.data;
};

export const disconnectInbodyAccount = async () => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(ENDPOINTS.PLATFORM.DISCONNECT_INBODY);

  return response.data;
};

export type InbodyDateTime = {
  id: string; // inbody datetime id, used as batchID
  date: string; // ISO date
  exists: number; // 1 = already imported
};

export const getInbodyDateTimes = async (customerId: string) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_INBODY_DATETIMES}${customerId}`
  );

  return response.data;
};

// datetimes: a single id or comma-separated ids
export const getInbodyData = async (customerId: string, datetimes: string) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_INBODY_DATA}${customerId}/${datetimes}`
  );

  return response.data;
};

export const getCustomerEmail = async (id: string) => {
  const client = await PlatformBaseApiNoAuth();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_CUSTOMER_EMAIL}${id}`
  );

  return response.data;
};

export const registerUser = async (data: any) => {
  const client = await PlatformBaseApiNoAuth();
  if (!client) {
    return null;
  }
  const response = await client.post(ENDPOINTS.PLATFORM.REGISTER_USER, data);

  return response.data;
};

export const inviteCostumer = async (data: any) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.post(`${ENDPOINTS.PLATFORM.INVITE_USER}`, data);

  return response.data;
};

export const deleteInvitedCostumer = async (email: string) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const payload = { email: email };
  const response = await client.delete(
    `${ENDPOINTS.PLATFORM.DELETE_INVITED_USER}`,
    { data: payload }
  );

  return response.data;
};

export const resendInvitationToCostumer = async (email: string) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const payload = { email: email };
  const response = await client.post(
    `${ENDPOINTS.PLATFORM.RESEND_INVITATION}`,
    payload
  );

  return response.data;
};

export const submitBiometricQuestionnaireToNutServer = async (data: any) => {
  const client = await NUTBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.post(
    ENDPOINTS.NUTRITION.SUBMIT_BIOMETRIC_QUESTIONNAIRE,
    data
  );
  return response.data;
};

export const submitLifestyleQuestionnaireToNutServer = async (data: any) => {
  const client = await NUTBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.post(
    ENDPOINTS.NUTRITION.SUBMIT_LIFESTYLE_QUESTIONNAIRE,
    data
  );
  return response.data;
};

export const submitBiometricQuestionnaireToPlatformServer = async (
  data: any
) => {
  const client = await PlatformBaseApiNoAuth();
  if (!client) {
    return null;
  }
  const response = await client.post(
    ENDPOINTS.PLATFORM.SUBMIT_BIOMETRIC_QUESTIONNAIRE,
    data
  );

  return response.data;
};

export const submitBiometricQuestionnaireToNUTServerFailed = async (
  data: any
) => {
  const client = await PlatformBaseApiNoAuth();
  if (!client) {
    return null;
  }
  const response = await client.post(
    ENDPOINTS.PLATFORM.SUBMIT_BIOMETRIC_QUESTIONNAIRE_TO_NUT_FAILED,
    data
  );

  return response.data;
};

export const requestPasswordReset = async (email: { email: string }) => {
  const client = await PlatformBaseApiNoAuth();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.REQUEST_RESET_PASSWORD}${email}`
  );

  return response.data;
};

export const PasswordReset = async (data: {
  password: string;
  passwordConfirm?: string;
  token?: string | null;
}) => {
  const client = await PlatformBaseApiNoAuth();
  if (!client) {
    return null;
  }
  const response = await client.post(ENDPOINTS.PLATFORM.RESET_PASSWORD, data);

  return response.data;
};

export const getPlatformData = async (customerID: string, role: number) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_PLATFORM_DATA}${customerID}`
  );

  return response.data;
};

/**
 * Fetches the new consultation-prep payload from the backend. Used for shape
 * inspection while we decide on adapter logic in `fetchConsultationPrep.ts`;
 * the consultation UI still reads from the mock until the shape stabilizes.
 * See claude_docs/CONSULTATION_BACKEND_BRIEF.md.
 */
export const getConsultation = async (customerID: string) => {
  let client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_CONSULTATION}${customerID}`
  );
  return response.data;
};

export const getHomePage = async (date: string, role: number) => {
  let client;
  if (role === 1) {
    client = await NUTBaseApi();
  } else {
    client = await NUTBaseApiAffiliateRequest();
  }
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.NUTRITION.FETCH_HOME_URL}` + "?date=" + `${date}`
  );
  return response.data;
};

export const getAllIntegrations = async (role: number) => {
  let client;
  if (role === 1) {
    client = await NUTBaseApi();
  } else {
    client = await NUTBaseApiAffiliateRequest();
  }

  if (!client) {
    return null;
  }

  const response = await client.get(
    ENDPOINTS.NUTRITION.INTEGRATIONS_STATUS_URL
  );
  return response.data;
};
export const getCustomerIngredient = async (id: string, role: number) => {
  let client;
  if (role === 1) {
    client = await NUTBaseApi();
  } else {
    client = await NUTBaseApiAffiliateRequest();
  }

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.NUTRITION.CUSTOMER_INGREDIENT}/${id}`
  );
  return response.data;
};

export const GetCustomerPersonalInfo = async (id: string) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_CUSTOMER_PROFILE}${id}`
  );

  return response.data;
};

export const getCustomerBiometricsQuestionnaireCompleted = async (
  id: string
) => {
  const selectedUser = await GetCustomerPersonalInfo(id);
  if (!selectedUser) {
    return false;
  }

  if (typeof selectedUser.biometrics_questionnaire_completed === "boolean") {
    return selectedUser.biometrics_questionnaire_completed;
  }

  if (!selectedUser.id_token) {
    return false;
  }

  const hostname = window.location.hostname;
  const config =
    hostname === "welcome.mypnoe.com" ||
    hostname === "welcome-staging.mypnoe.com"
      ? CONFIG.PROD
      : CONFIG.DEV;

  const response = await axios.get(
    `${config.NUT_BASE_URL}${ENDPOINTS.NUTRITION.SETTINGS_URL}`,
    {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        Accept: "application/json",
        "X-GO": "1",
        "Accept-Version": "1.0.0",
        "Action-Source": "welcome",
        Authorization: `Bearer ${selectedUser.id_token}`,
      },
    }
  );

  return Boolean(response.data?.biometrics_questionnaire_completed);
};

export const EditCustomerPersonalInfo = async (id: string, data: any) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.post(
    `${ENDPOINTS.PLATFORM.EDIT_CUSTOMER_PROFILE}${id}`,
    data
  );
  return response.data;
};

export const getCountries = async () => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(`${ENDPOINTS.PLATFORM.GET_COUNTRIES}`);
  return response.data;
};

export const getLogicCategories = async () => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_LOGIC_CATEGORIES}`
  );
  return response.data;
};

export const getLogicCategoriesNoAuth = async () => {
  const client = await PlatformBaseApiNoAuth();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_LOGIC_CATEGORIES}`
  );
  return response.data;
};

export const GetBiometricQuestionnaire = async (role: number) => {
  let client;
  if (role === 1) {
    client = await NUTBaseApi();
  } else {
    client = await NUTBaseApiAffiliateRequest();
  }

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.NUTRITION.CUSTOMER_PERSONAL_INFO}`
  );
  return response.data;
};

// Same transport split as GetBiometricQuestionnaire: a customer (role 1)
// edits their own profile with their own token; an affiliate edits a client's
// with the per-tab customer token. Without the split a customer's save went
// out on the affiliate transport, which has no customer token and hard-
// redirects to /login (→ Dashboard) instead of saving.
export const EditBiometricQuestionnaire = async (data: any, role: number) => {
  const client =
    role === 1 ? await NUTBaseApi() : await NUTBaseApiAffiliateRequest();
  if (!client) {
    return null;
  }
  const response = await client.put(
    `${ENDPOINTS.NUTRITION.EDIT_CUSTOMER_PERSONAL_INFO}`,
    data
  );
  return response.data;
};

export const getNutritionQuestionnaire = async (role: number) => {
  let client;
  if (role === 1) {
    client = await NUTBaseApi();
  } else {
    client = await NUTBaseApiAffiliateRequest();
  }

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.NUTRITION.CUSTOMER_QUESTIONNAIRE_NUTRITION}`
  );

  return response.data;
};

export const GetBiomarkers = async (id: string) => {
  let client;
  client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_COSTUMER_BIOMARKERS}${id}`
  );
  return response.data;
};

export const DeleteCostumerBiomarker = async (
  userBiomarkerID: string,
  biomarkerID: string,
  customerID: string
) => {
  let client;
  client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const payload = {
    id: userBiomarkerID,
    biomarkerId: biomarkerID,
    userId: customerID,
  };

  const response = await client.post(
    `${ENDPOINTS.PLATFORM.DELETE_COSTUMER_BIOMARKERS}${customerID}`,
    payload
  );
  return response.data;
};

// For qualitative biomarkers pass `qvalue` (the chosen qoptions string) and
// omit the numeric value — the payload sends `qvalue` instead of `newValue`.
// The backend inserts in batch mode with no validation, so the caller must
// guarantee the value is valid.
export const UpdateCostumerBiomarker = async (
  biomarkerID: string,
  customerID: string,
  biomarkerDate: string,
  biomarkerValue: number,
  qvalue?: string
) => {
  let client;
  client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const payload = {
    biomarkerId: biomarkerID,
    userId: customerID,
    date: biomarkerDate,
    ...(qvalue !== undefined ? { qvalue } : { newValue: biomarkerValue }),
  };

  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPDATE_COSTUMER_BIOMARKERS}${customerID}`,
    payload
  );
  return response.data;
};

// Batch variant of UpdateBiomarker: the payload is an array of biomarker
// objects in the exact shape returned by the InBody GetData endpoint — no
// normalization needed.
export const UpdateCostumerBiomarkers = async (
  customerID: string,
  biomarkers: any[]
) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPDATE_COSTUMER_BIOMARKERS_MULTIPLE}`,
    biomarkers
  );
  return response.data;
};

// Batch per-biomarker update (UpdateBiomarkers): each item carries its own
// userId + date, plus newValue (numeric), qvalue (qualitative), or range_*
// bounds (range). The backend inserts without validation, so the caller must
// ensure the values are valid.
export const UpdateCostumerBiomarkersBatch = async (biomarkers: any[]) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPDATE_COSTUMER_BIOMARKERS_BATCH}`,
    biomarkers
  );
  return response.data;
};

export const ImportClient = async (data: any) => {
  let client;
  client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  console.log("Importing client data:", data);

  const response = await client.post(
    `${ENDPOINTS.PLATFORM.IMPORT_COSTUMER_KERAGON}`,
    data
  );
  return response.data;
};

// export const UploadCostumerBiomarker = async (
//   customerID: string,
//   file: File
// ) => {
//   let client;
//   client = await PnoeBaseApi();
//
//   if (!client) {
//     return null;
//   }
//
//   // Create FormData and append the file
//   const formData = new FormData();
//   formData.append("file", file);
//
//   // Send FormData directly, do NOT set Content-Type manually
//   const response = await client.post(
//     `${ENDPOINTS.PLATFORM.UPLOAD_COSTUMER_BIOMARKERS}${customerID}`,
//     formData
//   );
//   return response.data;
// };

export const UploadCostumerBiomarker = async (
  customerID: string,
  file: File
) => {
  let client;
  client = await PnoeAgentApi();

  if (!client) {
    return null;
  }

  // Create FormData and append the file
  const formData = new FormData();
  formData.append("file", file);
  formData.append("agentSlug", "biomarker_matcher_1");

  // The agent client defaults to a JSON Content-Type, which would strip the
  // multipart boundary and make the backend see no file. Drop the header for
  // this request so the browser sets `multipart/form-data; boundary=...`.
  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPLOAD_COSTUMER_BIOMARKERS_AGENT}/${customerID}`,
    formData,
    {
      transformRequest: (data, headers) => {
        // axios v1 passes an AxiosHeaders instance (use .delete); older/plain
        // objects fall back to the delete operator.
        if (headers && typeof (headers as any).delete === "function") {
          (headers as any).delete("Content-Type");
        } else if (headers) {
          delete (headers as any)["Content-Type"];
          delete (headers as any)["content-type"];
        }
        return data;
      },
    }
  );
  return response.data;
};

// Polls the extractor agent for a job's status. Returns
// { jobId, status: "Running" | "Failed" | "Done", createdAt, result?, error? }.
export const GetCostumerBiomarkerStatus = async (jobId: string) => {
  let client;
  client = await PnoeAgentApi();

  if (!client) {
    return null;
  }

  const response = await client.get(
    `${ENDPOINTS.PLATFORM.UPLOAD_COSTUMER_BIOMARKERS_STATUS_AGENT}${jobId}`
  );
  return response.data;
};

export const UpdateCostumerSelectedMetrics = async (
  customerID: string,
  data: string[]
) => {
  let client;
  client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const payload = {
    id: customerID,
    list: data,
  };

  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPDATE_COSTUMER_SELECTED_METRICS}`,
    payload
  );
  return response.data;
};

export const UpdateCostumerSelectedBiomarkers = async (
  customerID: string,
  data: string[]
) => {
  let client;
  client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const payload = {
    id: customerID,
    list: data,
  };

  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPDATE_COSTUMER_SELECTED_BIOMARKERS}`,
    payload
  );
  return response.data;
};

export const GetBiomarkersText = async () => {
  let client;
  client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_BIOMARKERS_TEXT}`
  );
  return response.data;
};

export const GetBiomarkersTextNoAuth = async () => {
  const client = await PlatformBaseApiNoAuth();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_BIOMARKERS_TEXT}`
  );
  return response.data;
};

export const GetGraphData = async (
  role: number,
  periodType: string,
  date: string,
  key: string,
  type: string
) => {
  let client;
  if (role === 1) {
    client = await NUTBaseApi();
  } else {
    client = await NUTBaseApiAffiliateRequest();
  }

  if (!client) {
    return null;
  }
  let url;
  if (type === "nut") {
    url = `${ENDPOINTS.NUTRITION.FETCH_GRAPHS_PAGE_DATA}/${periodType}-nutrition?date=${date}&key=${key}`;
  } else {
    url = `${ENDPOINTS.NUTRITION.FETCH_GRAPHS_PAGE_DATA}/${periodType}-${key}?date=${date}`;
  }

  const response = await client.get(url);
  return response.data;
};

export const getAllServicesForSpecificType = async (type: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_ALL_SERVICES}${type}`
  );

  return response.data;
};

export const loadServiceTemplate = async (id: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(`${ENDPOINTS.PLATFORM.LOAD_SERVICE}${id}`);

  return response.data;
};

export const getUserSavedServices = async () => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_USER_SAVED_SERVICES}`
  );

  return response.data;
};

export const getUserSavedLogics = async () => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(`${ENDPOINTS.PLATFORM.LOAD_USER_LOGIC}`);

  return response.data;
};

export const getUserLabPanels = async () => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_USER_LAB_PANELS}`
  );

  return response.data;
};

export const getProviderLabPanels = async (provider?: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_PROVIDER_LAB_PANELS}${provider ?? ""}`
  );

  return response.data;
};

export const getProviderBiomarkers = async (
  provider: string,
  kind?: number | null,
  category?: number | null
) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  // Backend route: GetProviderBiomarkers/{provider}/{kind?}/{category?}.
  // kind/category are optional trailing segments (category requires kind).
  let path = `${ENDPOINTS.PLATFORM.GET_PROVIDER_BIOMARKERS}${provider}`;
  if (kind !== undefined && kind !== null) {
    path += `/${kind}`;
    if (category !== undefined && category !== null) {
      path += `/${category}`;
    }
  }

  const response = await client.get(path);

  return response.data;
};

export const updateUserLabPanel = async (data: any) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPDATE_USER_LAB_PANEL}`,
    data
  );

  return response.data;
};

export const deleteUserLabPanel = async (id: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.delete(
    `${ENDPOINTS.PLATFORM.DELETE_USER_LAB_PANEL}${id}`
  );

  return response.data;
};

// ---------------------------------------------------------------------------
// Keragon EHR sync (affiliate token, platform API)

// The docs spell the webhook field `URL`, the API returns `url`; `active`
// comes back as a number. Normalise once here so the UI can rely on the
// documented shape.
const normalizeKeragonWorkflow = (raw: any): KeragonWorkflow => ({
  id: String(raw?.id ?? ""),
  companyID: String(raw?.companyID ?? raw?.companyId ?? ""),
  name: String(raw?.name ?? ""),
  URL: String(raw?.URL ?? raw?.url ?? ""),
  active: Number(raw?.active) === 1 ? 1 : 0,
});

/** All company workflows (webhook URLs) for the logged-in affiliate. */
export const getKeragonWorkflows = async (): Promise<KeragonWorkflow[]> => {
  const client = await PlatformBaseApi();
  if (!client) {
    return [];
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.KERAGON_GET_COMPANY_WORKFLOWS}`
  );
  return Array.isArray(response.data)
    ? response.data.map(normalizeKeragonWorkflow)
    : [];
};

export const getKeragonWorkflow = async (id: string) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.KERAGON_GET_COMPANY_WORKFLOW}${id}`
  );
  return response.data;
};

/** Upsert: `id` null inserts, otherwise updates that workflow. */
export const addKeragonWorkflow = async (data: KeragonWorkflowUpsert) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.post(
    `${ENDPOINTS.PLATFORM.KERAGON_ADD_COMPANY_WORKFLOW}`,
    data
  );
  return response.data;
};

export const deleteKeragonWorkflow = async (id: string) => {
  const client = await PlatformBaseApi();
  if (!client) {
    return null;
  }
  const response = await client.delete(
    `${ENDPOINTS.PLATFORM.KERAGON_DELETE_COMPANY_WORKFLOW}${id}`
  );
  return response.data;
};

/** Keragon answered the proxied trigger with a non-2xx status. */
export class KeragonTriggerError extends Error {
  constructor(public status: number) {
    super(`Keragon returned ${status}`);
  }
}

/**
 * Starts the Keragon lab-import workflow for a patient. `url` is the Keragon
 * signal URL the affiliate stored in Settings. Keragon rejects browser CORS
 * preflights, so the call goes through our authorised /Webhook/Trigger proxy,
 * which forwards `body` (a raw string) server-to-server and relays Keragon's
 * reply. An HTTP error from the proxy itself (e.g. a non-allowlisted host)
 * surfaces as an axios error.
 */
export const triggerKeragonWorkflow = async (url: string, trigger: KeragonTrigger) => {
  const client = await PnoeAgentApi();
  if (!client) {
    throw new Error("No API client");
  }
  const request: KeragonProxyRequest = { url, body: JSON.stringify(trigger) };
  const response = await client.post<KeragonProxyResponse>(
    `${ENDPOINTS.PLATFORM.KERAGON_WEBHOOK_TRIGGER}`,
    request
  );
  if (!response.data?.ok) {
    throw new KeragonTriggerError(Number(response.data?.status ?? 0));
  }
};

/**
 * Polls the lab-import result for a patient (same identity as the trigger).
 * Anonymous on the backend; goes through the agent client like the extractor
 * status poll. axios `params` URL-encodes non-Latin names correctly.
 */
export const getKeragonResultStatus = async (
  patient: KeragonPatient
): Promise<KeragonJobResponse | null> => {
  const client = await PnoeAgentApi();
  if (!client) {
    return null;
  }
  const response = await client.get(`${ENDPOINTS.PLATFORM.KERAGON_RESULT_STATUS}`, {
    params: patient,
  });
  return response.data;
};

export const finalizeCustomerLabPanel = async (panelId: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.FINALIZE_CUSTOMER_LAB_PANEL}${panelId}`
  );

  return response.data;
};

// Re-submits a lab panel that Junction rejected or failed (panelStatus
// OrderFailed) after the payment already went through. A server-side
// operation keyed on the customer lab panel id alone: on success the panel
// moves to OrderReceived (5), on another failure it stays OrderFailed (11),
// and an unknown id is a 422. Returns the updated panel.
export const retryLabPanel = async (panelId: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.RETRY_LAB_PANEL}${panelId}`
  );

  return response.data;
};

// Approves a lab panel's results: the backend creates UserBiomarkers for the
// customer and returns the panel with updated statuses & results. `panelId` is
// the customer lab panel id (same id finalize uses).
export const approveCustomerLabPanel = async (panelId: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.APPROVE_CUSTOMER_LAB_PANEL}${panelId}`
  );

  return response.data;
};

// Rejects a lab panel's results — server-side status update only, needs just the
// customer lab panel id. Panel status becomes ApprovalRejected.
export const rejectCustomerLabPanel = async (panelId: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.REJECT_CUSTOMER_LAB_PANEL}${panelId}`
  );

  return response.data;
};

export const deleteUserLogicById = async (id: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }
  const response = await client.get(`${ENDPOINTS.PLATFORM.DELETE_LOGIC}${id}`);

  return response.data;
};

export const getServiceById = async (id: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_SERVICE_BY_ID}${id}`
  );

  return response.data;
};

export const getLogicBuilderServiceById = async (arrayIds: string[]) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const response = await client.post(
    `${ENDPOINTS.PLATFORM.GET_LOGIC_BUILDER_SERVICE_BY_ID}`,
    arrayIds
  );

  return response.data;
};

/**
 * History listings — page-numbered. `userid` narrows to one client; omitted,
 * the page covers every client of the logged-in company. Empty values are
 * dropped so the company scope never sends `userid=`.
 */
const historyListQuery = (params: HistoryListParams) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );

export const getHistoryServices = async (
  params: HistoryListParams
): Promise<HistoryPage<HistoryServiceListItem> | null> => {
  const client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.get(ENDPOINTS.PLATFORM.GET_HISTORY_SERVICES, {
    params: historyListQuery(params),
  });
  return response.data;
};

export const getHistoryService = async (
  serviceId: string
): Promise<HistoryServiceDetail | null> => {
  const client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_HISTORY_SERVICE}${serviceId}`
  );
  return response.data;
};

export const getHistoryPrescriptions = async (
  params: HistoryListParams
): Promise<HistoryPage<HistoryPrescriptionListItem> | null> => {
  const client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.get(
    ENDPOINTS.PLATFORM.GET_HISTORY_PRESCRIPTIONS,
    { params: historyListQuery(params) }
  );
  return response.data;
};

export const getHistoryPrescription = async (
  prescriptionId: string
): Promise<HistoryPrescriptionDetail | null> => {
  const client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_HISTORY_PRESCRIPTION}${prescriptionId}`
  );
  return response.data;
};

/**
 * Lab panels (customer orders) of the company or one client. Rows are the
 * same customer-panel entity the orders screens resolve statuses on.
 */
export const getHistoryLabPanels = async (
  params: HistoryListParams
): Promise<HistoryPage<any> | null> => {
  const client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.get(ENDPOINTS.PLATFORM.GET_HISTORY_LAB_PANELS, {
    params: historyListQuery(params),
  });
  return response.data;
};

/** One lab panel with its items, by the listing row's id. */
export const getHistoryLabPanel = async (panelId: string): Promise<any> => {
  const client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_HISTORY_LAB_PANEL}${panelId}`
  );
  return response.data;
};

export const getHistoryInbody = async (
  params: HistoryListParams
): Promise<HistoryPage<HistoryInbodyListItem> | null> => {
  const client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.get(ENDPOINTS.PLATFORM.GET_HISTORY_INBODY, {
    params: historyListQuery(params),
  });
  return response.data;
};

/** The biomarkers of one InBody test, by the listing row's id. */
export const getHistoryInbodyRecord = async (
  recordId: string
): Promise<HistoryInbodyBiomarker[] | null> => {
  const client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GET_HISTORY_INBODY_RECORD}${recordId}`
  );
  return response.data;
};

export const generateLogicServices = async (costumerID: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const response = await client.get(
    `${ENDPOINTS.PLATFORM.GENERATE_LOGIC_SERVICES}${costumerID}`
  );

  return response.data;
};

export const updateService = async (data: any) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPDATE_SERVICE}`,
    data
  );

  return response.data;
};

export const updateLogic = async (data: any) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPDATE_LOGIC}`,
    data
  );

  return response.data;
};

export const updateCustomerPrescription = async (data: any) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPDATE_CUSTOMER_SERVICE}`,
    data
  );

  return response.data;
};

export const deleteCustomerPrescription = async (
  customerPrescriptionId: any
) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const response = await client.delete(
    `${ENDPOINTS.PLATFORM.DELETE_USER_PRESCRIPTION}/${customerPrescriptionId}`
  );

  return response.data;
};

// Delete a single item (service or lab panel) from a customer prescription.
// The backend enforces the paid-panel rule: a lab panel with panelStatus past
// PaymentFailed is not removed and the call returns HTTP 422. Returns the
// number of records deleted (0 = nothing removed).
export const deleteCustomerPrescriptionItem = async (
  customerPrescriptionId: string,
  itemId: string
) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const response = await client.delete(
    `${ENDPOINTS.PLATFORM.DELETE_USER_PRESCRIPTION}/${customerPrescriptionId}/${itemId}`
  );

  return response.data;
};

export const updateLogicStatus = async (id: string, status: number) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const response = await client.get(
    `${ENDPOINTS.PLATFORM.UPDATE_LOGIC_STATUS}${id}/${status}`
  );

  return response.data;
};

export const deleteServiceById = async (id: string) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  const response = await client.get(
    `${ENDPOINTS.PLATFORM.DELETE_SERVICE}${id}`
  );

  return response.data;
};

export const toggleSystemApprovals = async (
  data: Record<string, string | number>
) => {
  const client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.post(ENDPOINTS.PLATFORM.TOGGLE_SYSTEM, data);
  return response.data;
};

export const resetApprovals = async (id: string) => {
  const client = await PlatformBaseApi();
  if (!client) return null;
  const response = await client.get(
    `${ENDPOINTS.PLATFORM.RESET_APPROVALS}${id}`
  );
  return response.data;
};

export const uploadServiceImage = async (serviceId: string, file: File) => {
  let client = await PlatformBaseApi();

  if (!client) {
    return null;
  }

  // Create FormData and append the file
  const formData = new FormData();
  formData.append("file", file);

  // Send FormData with proper headers for file upload
  // Delete Content-Type header to let browser set multipart/form-data with boundary automatically
  const response = await client.post(
    `${ENDPOINTS.PLATFORM.UPLOAD_SERVICE_IMAGE}${serviceId}`,
    formData,
    {
      headers: {
        "Content-Type": undefined,
      },
    }
  );

  return response.data;
};

/* -------------------------------------------------------------------------- */
/* PNOĒ Breath Data API — breath tests (measurements)                         */
/* -------------------------------------------------------------------------- */

const BREATH = ENDPOINTS.BREATH;
const breathMeasurementPath = (measurementId: string, suffix = "") =>
  `${BREATH.MEASUREMENT}${measurementId}${suffix}`;

/**
 * Which fields of a breath PATCH body are decimals, and so must be sent as
 * JSON **strings**.
 *
 * The service rejects a JSON number outright — `"Value error, decimal values
 * must be JSON strings"` — and the read models type these as `DecimalString`
 * for the same reason. Converting here rather than in each panel means the
 * rule lives with the endpoints that impose it, and a future caller cannot
 * miss it.
 *
 * `age` is absent on purpose: it is an integer, not a decimal.
 */
const BREATH_DECIMAL_FIELDS = [
  "weight_kg",
  "height_cm",
  "fvc_lt",
  "elapsed_time",
  "watts",
  "speed",
  "grade",
] as const;

const withDecimalStrings = (body: object): object =>
  Object.fromEntries(
    Object.entries(body).map(([key, value]) =>
      (BREATH_DECIMAL_FIELDS as readonly string[]).includes(key) &&
      typeof value === "number"
        ? [key, toDecimalString(value)]
        : [key, value]
    )
  );

/**
 * Catalogs (enum display labels) plus the participant and protocol option lists,
 * in a single payload — the documented namespace exposes them as one resource.
 *
 * Two hooks need it (the listing wants catalogs, the detail wants the option
 * lists), so the in-flight promise is shared rather than fetched twice. The
 * content is static config; it is re-fetched on a full page load.
 */
let breathConfigurationPromise: Promise<PnoeBreathConfiguration | null> | null =
  null;

export const getBreathConfiguration = async (): Promise<PnoeBreathConfiguration | null> => {
  if (!breathConfigurationPromise) {
    breathConfigurationPromise = (async () => {
      const client = await BreathBaseApi();
      if (!client) return null;
      const response = await client.get(BREATH.CONFIGURATION);
      return response.data as PnoeBreathConfiguration;
    })().catch((error) => {
      // Don't cache a failure — the next mount should retry.
      breathConfigurationPromise = null;
      throw error;
    });
  }
  return breathConfigurationPromise;
};

// Cursor-paginated. Pass `cursor` from the previous page's `next_cursor`;
// `limit` caps at 100 server-side.
//
// `asSelectedClient` signs with the selected client's token instead of the
// logged-in user's: the service then scopes the listing to that client, the
// same way it does for a customer on their own page, with no `customer_id`.
export const getPnoeMeasurements = async (
  params: Record<string, unknown> = {},
  { asSelectedClient = false }: { asSelectedClient?: boolean } = {}
) => {
  const client = asSelectedClient
    ? await BreathBaseApiAffiliateRequest()
    : await BreathBaseApi();
  if (!client) return null;
  // Drop empty values so we never send `?status=` and trip the 422 validator.
  const query = Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  );
  // The breath service stores customer GUIDs in upper case and matches this
  // filter case-sensitively; the platform's ids are lower case. Callers pass
  // the platform's id and the boundary adapts it.
  if (typeof query.customer_id === "string") {
    query.customer_id = query.customer_id.toUpperCase();
  }
  const response = await client.get(BREATH.MEASUREMENTS, { params: query });
  return response.data;
};

export const getPnoeMeasurement = async (measurementId: string) => {
  const client = await BreathBaseApi();
  if (!client) return null;
  const response = await client.get(breathMeasurementPath(measurementId));
  return response.data;
};

// Transitions a non-completed measurement to `completed` and enqueues a report
// calculation. Surfaced as "Complete" (in progress) or "Restore" (invalidated).
export const completePnoeMeasurement = async (measurementId: string) => {
  const client = await BreathBaseApi();
  if (!client) return null;
  const response = await client.post(
    breathMeasurementPath(measurementId, BREATH.MEASUREMENT_COMPLETE)
  );
  return response.data;
};

// Transitions to `invalidated` and unpublishes the report. Surfaced as "Abort"
// (in progress) or "Invalidate" (completed). There is no DELETE — this is it.
export const invalidatePnoeMeasurement = async (measurementId: string) => {
  const client = await BreathBaseApi();
  if (!client) return null;
  const response = await client.post(
    breathMeasurementPath(measurementId, BREATH.MEASUREMENT_INVALIDATE)
  );
  return response.data;
};

// Only allowed on `completed` measurements. Any change other than customer_name
// triggers a report recalculation — the response carries the new report_status.
//
// `lifestyle_answers` is a nested map of question key → choice value.
// `withDecimalStrings` only touches top-level numbers, so it passes through
// verbatim — which is right, since choice values are strings.
export const updatePnoeParticipant = async (
  measurementId: string,
  body: PnoeParticipantUpdate | object
) => {
  const client = await BreathBaseApi();
  if (!client) return null;
  const response = await client.patch(
    breathMeasurementPath(measurementId, BREATH.MEASUREMENT_PARTICIPANT),
    withDecimalStrings(body)
  );
  return response.data;
};

// RMR parameters only; the AMR ones are fixed before the measurement starts.
export const updatePnoeProtocol = async (
  measurementId: string,
  body: object
) => {
  const client = await BreathBaseApi();
  if (!client) return null;
  const response = await client.patch(
    breathMeasurementPath(measurementId, BREATH.MEASUREMENT_PROTOCOL),
    withDecimalStrings(body)
  );
  return response.data;
};

export const getPnoeMeasurementPhases = async (
  measurementId: string,
  params: Record<string, unknown> = {}
) => {
  const client = await BreathBaseApi();
  if (!client) return null;
  const response = await client.get(
    breathMeasurementPath(measurementId, BREATH.MEASUREMENT_PHASES),
    { params }
  );
  return response.data;
};

/**
 * Correct several phases at once — `PhaseBatchUpdate`, keyed by phase id.
 *
 * The batch is the only way to move a phase: the per-phase path this replaced
 * no longer exists. It is also the right shape for the job. The service
 * validates every entry together and rejects all of them if one is bad, so a
 * refused boundary can no longer leave the set half-moved, and a changed batch
 * enqueues exactly one report calculation rather than one per phase.
 *
 * Each entry accepts only `elapsed_time`, `watts`, `speed` and `grade`; the
 * phase name and type are immutable and are refused. Send only the phases that
 * changed — re-serialising untouched values would round them to three decimals
 * and read as an edit.
 *
 * `withDecimalStrings` walks a flat body, so it is applied per entry: the
 * decimals live one level down, and the service rejects a JSON number.
 */
export const updatePnoeMeasurementPhases = async (
  measurementId: string,
  phases: Record<string, Record<string, number | string | null>>
) => {
  const client = await BreathBaseApi();
  if (!client) return null;
  const body = Object.fromEntries(
    Object.entries(phases).map(([phaseId, patch]) => [
      phaseId,
      withDecimalStrings(patch),
    ])
  );
  const response = await client.patch(
    breathMeasurementPath(measurementId, BREATH.MEASUREMENT_PHASES),
    body
  );
  return response.data;
};

export const getPnoeVo2Data = async (
  measurementId: string,
  params: Record<string, unknown> = {}
) => {
  const client = await BreathBaseApi();
  if (!client) return null;
  const response = await client.get(
    breathMeasurementPath(measurementId, BREATH.MEASUREMENT_VO2_DATA),
    { params }
  );
  return response.data;
};

// The server applies no smoothing — what comes back is the raw series as CSV.
// Read as a blob so the caller can hand it straight to a download.
export const exportPnoeVo2Data = async (measurementId: string) => {
  const client = await BreathBaseApi();
  if (!client) return null;
  const response = await client.get(
    breathMeasurementPath(measurementId, BREATH.MEASUREMENT_VO2_EXPORT),
    { responseType: "blob" }
  );
  return response;
};

/**
 * Walks a cursor-paginated Breath API collection to the end.
 *
 * The vo2 series caps `limit` at 100, so a 20-minute test is a few dozen
 * sequential round-trips. `maxPages` is a hard stop so a runaway cursor can
 * never hang the page, and `onProgress` lets the caller show how far it got.
 */
export const fetchAllBreathPages = async <T>(
  fetchPage: (params: { limit: number; cursor?: string }) => Promise<any>,
  options: {
    limit?: number;
    maxPages?: number;
    onProgress?: (loaded: number) => void;
  } = {}
): Promise<{ data: T[]; truncated: boolean }> => {
  const limit = options.limit ?? 100;
  const maxPages = options.maxPages ?? 100;
  const all: T[] = [];
  let cursor: string | undefined;
  let pages = 0;

  do {
    const page = await fetchPage({ limit, cursor });
    const rows: T[] = Array.isArray(page?.data) ? page.data : [];
    all.push(...rows);
    options.onProgress?.(all.length);
    cursor = page?.next_cursor || undefined;
    pages += 1;
  } while (cursor && pages < maxPages);

  return { data: all, truncated: Boolean(cursor) };
};
